/* Borderline winter 26/27 · header behaviour (P0: snippets/bl-header-extras.liquid, assets/bl-header.css).
   - <bl-explore>: the Explore door opens its panel after a short hover (fine pointer), on click and from the
     keyboard; Escape, leaving it (after a grace that holds while the pointer heads into the panel) or a department
     panel opening closes it, and it closes an open department panel as it opens
   - <bl-store>: the store chip's dropdown of the stores (R17b; <bl-store-list> = the same list open in the phone
     drawer); the choice is remembered and every [data-bl-store-name] / [data-bl-store-short] on the page follows it;
     html[data-bl-store] and a 'bl:store' event tell the rest (the contract: "the store choice" below)
   - <bl-saved>: the saved list (bl-card.js keeps it in localStorage 'bl-wish'), the counts in the header and drawer
   - keeps Horizon's --top-row-height current while the home header grows and shrinks (its underlays use it)
   - marks body.bl-drawer-open while the menu drawer is open, so the page's own sticky bars step out (bl-header.css)
   - <bl-drawer-menu>: the menu drawer's department lists, fetched on the first press of the burger (R2 10/21) */

const WISH_KEY = 'bl-wish';
const STORE_KEY = 'bl-store';
const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

/** The last pointer pressed anywhere ('mouse' | 'pen' | 'touch'), for clicks that do not say which pointer made them. */
let lastPointerType = '';
document.addEventListener('pointerdown', (event) => (lastPointerType = event.pointerType), { capture: true, passive: true });

/** @returns {Array<{h: string, t: string, v: string, u: string, i: string}>} */
function readWishlist() {
  if (window.BL?.wishlist) return window.BL.wishlist.read();
  try {
    const list = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
}

/** @param {string} handle */
function removeFromWishlist(handle) {
  const list = readWishlist().filter((item) => item.h !== handle);
  if (window.BL?.wishlist) {
    window.BL.wishlist.write(list);
    return;
  }
  try {
    localStorage.setItem(WISH_KEY, JSON.stringify(list));
  } catch (error) {
    /* storage blocked */
  }
  document.dispatchEvent(new CustomEvent('bl:wish', { detail: { count: list.length } }));
}

function syncWishCounts() {
  const count = readWishlist().length;
  for (const badge of document.querySelectorAll('[data-bl-wish-count]')) {
    badge.textContent = String(count);
    if (badge instanceof HTMLElement) badge.hidden = count === 0;
  }
}

/** Close every open header popover except the one given. */
function closePopovers(except) {
  for (const element of document.querySelectorAll('bl-explore[open], bl-store[open], bl-saved[open]')) {
    if (element !== except && typeof element.close === 'function') element.close(false);
  }
}

/* ---------- a small disclosure: a button that shows a panel, closes on Escape and outside presses ---------- */
class BlDisclosure extends HTMLElement {
  /** @type {AbortController | null} */
  controller = null;

  /** @type {AbortController | null} */
  openController = null;

  get button() {
    return /** @type {HTMLElement | null} */ (this.querySelector(':scope > [aria-controls]'));
  }

  get panel() {
    const id = this.button?.getAttribute('aria-controls');
    return id ? /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(id)}`)) : null;
  }

  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.button?.addEventListener('click', (event) => this.onButtonClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.openController?.abort();
    this.openController = null;
  }

  /** @param {MouseEvent} event */
  onButtonClick(event) {
    if (!this.panel) return;
    event.preventDefault();
    if (this.hasAttribute('open')) this.close(false);
    else this.open();
  }

  open() {
    const panel = this.panel;
    if (!panel || this.hasAttribute('open')) return;
    closePopovers(this);
    panel.hidden = false;
    this.setAttribute('open', '');
    this.button?.setAttribute('aria-expanded', 'true');
    this.openController?.abort();
    this.openController = new AbortController();
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.close(false);
      },
      { signal: this.openController.signal, capture: true }
    );
    this.onOpen();
  }

  /** @param {boolean} restoreFocus */
  close(restoreFocus) {
    this.openController?.abort();
    this.openController = null;
    if (!this.hasAttribute('open')) return;
    const panel = this.panel;
    if (panel) panel.hidden = true;
    this.removeAttribute('open');
    this.button?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.button?.focus();
  }

  onOpen() {}

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      event.stopPropagation();
      this.close(true);
    }
  }

  /** @param {FocusEvent} event */
  onFocusOut(event) {
    if (!this.hasAttribute('open')) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    if (next === null) return;
    this.close(false);
  }
}

/* ---------- Explore ---------- */
/* hover intent, the department row's rules (assets/header-menu.js): the panel opens after a short rest on the door (a
   pass on the way to search / store / cart must not flash it) and stays while the pointer is on its way into it — the
   panel hangs under the whole row and its links start at the far left, so a straight line from the door runs along
   the row for a long way */
const EXPLORE_OPEN_DWELL_MS = 150;
const EXPLORE_CLOSE_GRACE_MS = 300;
const EXPLORE_RECHECK_MS = 50;

class BlExplore extends BlDisclosure {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #leaveTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #enterTimer;

  /** @type {MutationObserver | null} */
  #menuObserver = null;

  /** recent pointer positions while the panel is open @type {Array<{ y: number, t: number }>} */
  #trail = [];

  connectedCallback() {
    super.connectedCallback();
    const signal = this.controller?.signal;
    this.addEventListener('pointerenter', this.#onEnter, { signal });
    this.addEventListener('pointerleave', this.#onLeave, { signal });

    // a department panel opening closes Explore (Horizon's header-menu flags itself aria-expanded)
    const menu = this.#menu();
    if (menu) {
      this.#menuObserver = new MutationObserver(() => {
        if (menu.getAttribute('aria-expanded') === 'true') this.close(false);
      });
      this.#menuObserver.observe(menu, { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#menuObserver?.disconnect();
    this.#menuObserver = null;
    clearTimeout(this.#leaveTimer);
    clearTimeout(this.#enterTimer);
  }

  #menu() {
    return /** @type {(HTMLElement & { close?: () => void }) | null} */ (this.closest('header-component')?.querySelector('header-menu') ?? null);
  }

  onOpen() {
    // one panel at a time: an open department panel closes now rather than after its grace
    this.#menu()?.close?.();
    this.#trail.length = 0;
    const signal = this.openController?.signal;
    document.addEventListener('pointermove', this.#onMove, { passive: true, signal });
  }

  /** @param {boolean} restoreFocus */
  close(restoreFocus) {
    clearTimeout(this.#enterTimer);
    clearTimeout(this.#leaveTimer);
    super.close(restoreFocus);
  }

  /** On a fine pointer the click follows the hover, so it must not close what the hover just opened (a tap on a
      touch-screen laptop, which has no hover, still toggles). */
  onButtonClick(event) {
    const pointer = /** @type {PointerEvent} */ (event).pointerType || lastPointerType;
    if (finePointer.matches && this.hasAttribute('open') && event.detail > 0 && pointer !== 'touch') {
      event.preventDefault();
      return;
    }
    super.onButtonClick(event);
  }

  #onEnter = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches || window.innerWidth < 990) return;
    clearTimeout(this.#leaveTimer);
    if (this.hasAttribute('open')) return;
    clearTimeout(this.#enterTimer);
    this.#enterTimer = setTimeout(() => this.open(), EXPLORE_OPEN_DWELL_MS);
  };

  #onLeave = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches) return;
    clearTimeout(this.#enterTimer);
    this.#scheduleClose(EXPLORE_CLOSE_GRACE_MS);
  };

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    const now = performance.now();
    this.#trail.push({ y: event.clientY, t: now });
    while (this.#trail.length > 1 && now - (this.#trail[0]?.t ?? now) > 150) this.#trail.shift();
  };

  /** @param {number} delay */
  #scheduleClose(delay) {
    clearTimeout(this.#leaveTimer);
    this.#leaveTimer = setTimeout(() => {
      if (!this.hasAttribute('open') || this.matches(':hover')) return;
      if (this.#headingIntoPanel()) {
        this.#scheduleClose(EXPLORE_RECHECK_MS);
        return;
      }
      this.close(false);
    }, delay);
  }

  /** Still above the panel (in the header row) and moving down towards it. */
  #headingIntoPanel() {
    const panelTop = this.panel?.getBoundingClientRect().top ?? 0;
    const now = performance.now();
    const recent = this.#trail.filter((point) => now - point.t <= 60);
    const first = recent[0];
    const last = recent[recent.length - 1];
    if (!first || !last || first === last || last.y >= panelTop) return false;
    return last.y - first.y >= 2;
  }
}

/* ---------- the store choice (R17b, 2026-10-29): the contract other modules read ----------
   key     'rh' | 'nm' | 'ot' — the store's code in lower case (the codes of the header's store list, = the short codes
           of Get it's rows: RH / NM / OT). The default (the header's first store, Richmond Hill) holds until the
           shopper picks one; nothing is guessed from location.
   html    <html data-bl-store="nm"> on every page from the header's first paint (an inline script in
           snippets/bl-header-extras), the default included; <html data-bl-store-chosen="true"> once the shopper picked.
   storage localStorage 'bl-store' = {"key":"nm","name":"Newmarket","short":"NM"} (name / short stay for bl-pdp.js,
           which matches Get it's rows by short or name); cookie bl_store=nm (path /, 1 year, SameSite=Lax) — Liquid
           cannot read cookies, the cookie is only for anything server-side later.
   event   document 'bl:store' (CustomEvent) on every change, detail {key, name, short, url, clearance, source};
           source 'pick' (the header / drawer list), 'page' (a store page's button), 'other-tab'. Not fired on load:
           read the attribute or window.BL.store.get() then.
   API     window.BL.store.get() → {key, name, short, url, clearance, chosen} | null; .set(key) → boolean; .list(). */
const STORE_COOKIE = 'bl_store';

/** @typedef {{ key: string, name: string, short: string, url: string, clearance: boolean }} BlStoreOption */

/** The stores the page offers (the header's list), by key. @returns {Map<string, BlStoreOption>} */
function storeOptions() {
  /** @type {Map<string, BlStoreOption>} */
  const options = new Map();
  for (const el of document.querySelectorAll('[data-bl-store-opt]')) {
    if (!(el instanceof HTMLElement)) continue;
    const key = el.dataset.blStoreOpt || '';
    if (!key || options.has(key)) continue;
    options.set(key, {
      key,
      name: el.dataset.name || key.toUpperCase(),
      short: el.dataset.short || key.toUpperCase(),
      url: el.dataset.url || '',
      clearance: el.dataset.clearance === 'true',
    });
  }
  return options;
}

/** The stored choice's key, from this contract or the old {name, short} value. */
function storedStoreKey() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    if (!stored || typeof stored !== 'object') return '';
    return String(stored.key || stored.short || '').toLowerCase();
  } catch (error) {
    return '';
  }
}

const storeChoice = {
  list: () => [...storeOptions().values()],

  /** @returns {(BlStoreOption & { chosen: boolean }) | null} */
  get() {
    const options = storeOptions();
    const stored = options.get(storedStoreKey());
    if (stored) return { ...stored, chosen: true };
    const fallback = options.get(document.querySelector('bl-store[data-default-key]')?.getAttribute('data-default-key') || '');
    const first = fallback || options.values().next().value;
    return first ? { ...first, chosen: false } : null;
  },

  /** @param {string} key @param {'pick' | 'page'} [source] */
  set(key, source = 'pick') {
    const option = storeOptions().get(String(key || '').toLowerCase());
    if (!option) return false;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ key: option.key, name: option.name, short: option.short }));
    } catch (error) {
      /* storage blocked: the page still follows the choice until it is left */
    }
    try {
      document.cookie = `${STORE_COOKIE}=${encodeURIComponent(option.key)}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (error) {
      /* cookies blocked */
    }
    paintStore(option.key);
    document.dispatchEvent(new CustomEvent('bl:store', { detail: { ...option, source } }));
    return true;
  },
};

/** The current store everywhere it shows: the html attribute, the chip's name / code, the lists' marks, the store
 * pages' "Make … my store" buttons. @param {string} [forceKey] - the key just set (storage may be blocked) */
function paintStore(forceKey) {
  let current = storeChoice.get();
  if (forceKey) {
    const forced = storeOptions().get(forceKey);
    if (forced) current = { ...forced, chosen: true };
  }
  if (!current) return;
  const root = document.documentElement;
  root.setAttribute('data-bl-store', current.key);
  if (current.chosen) root.setAttribute('data-bl-store-chosen', 'true');
  else root.removeAttribute('data-bl-store-chosen');
  for (const label of document.querySelectorAll('[data-bl-store-name]')) label.textContent = current.name;
  for (const label of document.querySelectorAll('[data-bl-store-short]')) label.textContent = current.short;
  for (const opt of document.querySelectorAll('[data-bl-store-opt]')) {
    if (!(opt instanceof HTMLElement)) continue;
    const on = opt.dataset.blStoreOpt === current.key;
    opt.classList.toggle('is-on', on);
    opt.querySelector('[data-bl-store-pick]')?.setAttribute('aria-pressed', String(on));
  }
  for (const chip of document.querySelectorAll('bl-store[data-label]')) {
    const label = chip.getAttribute('data-label') || '';
    if (label) chip.querySelector(':scope > .bl-store__btn')?.setAttribute('aria-label', label.replace('[store]', current.name));
  }
  for (const button of document.querySelectorAll('[data-bl-store-set]')) {
    const on = current.chosen && button.getAttribute('data-bl-store-set') === current.key;
    button.setAttribute('aria-pressed', String(on));
  }
}

/** a pick in any list (header dropdown, drawer list) or a store page's button */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const pick = target?.closest('[data-bl-store-pick]');
  if (pick) {
    const key = pick.closest('[data-bl-store-opt]')?.getAttribute('data-bl-store-opt') || '';
    storeChoice.set(key, 'pick');
    const chip = pick.closest('bl-store');
    if (chip instanceof BlDisclosure) chip.close(true);
    return;
  }
  const set = target?.closest('[data-bl-store-set]');
  if (set) storeChoice.set(set.getAttribute('data-bl-store-set') || '', 'page');
});

/* ---------- the store chip: a dropdown of the stores ---------- */
class BlStore extends BlDisclosure {
  /** opened from the keyboard (Enter / Space / ArrowDown on the chip): focus goes into the list */
  #keyboard = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.controller?.signal;
    this.button?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowDown' || this.hasAttribute('open')) return;
        event.preventDefault();
        event.stopPropagation();
        this.#keyboard = true;
        this.open();
      },
      { signal }
    );
    paintStore();
  }

  /** @param {MouseEvent} event */
  onButtonClick(event) {
    this.#keyboard = event.detail === 0;
    super.onButtonClick(event);
  }

  get picks() {
    return /** @type {HTMLElement[]} */ ([...(this.panel?.querySelectorAll('[data-bl-store-pick]') || [])]);
  }

  onOpen() {
    paintStore();
    if (!this.#keyboard) return;
    this.#keyboard = false;
    const picks = this.picks;
    (picks.find((pick) => pick.getAttribute('aria-pressed') === 'true') || picks[0])?.focus();
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    super.onKeydown(event);
    if (!this.hasAttribute('open') || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const picks = this.picks;
    const at = picks.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (at < 0 && document.activeElement !== this.button) return;
    event.preventDefault();
    let next = 0;
    if (event.key === 'End') next = picks.length - 1;
    else if (event.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(picks.length - 1, at + 1);
    else if (event.key === 'ArrowUp') next = at <= 0 ? 0 : at - 1;
    picks[next]?.focus();
  }
}

/* the phone drawer's open list: only paints (the picks are the document listener's) */
class BlStoreList extends HTMLElement {
  connectedCallback() {
    paintStore();
  }
}

window.BL = window.BL || {};
window.BL.store = storeChoice;

/* ---------- saved items ---------- */
class BlSaved extends BlDisclosure {
  connectedCallback() {
    super.connectedCallback();
    const signal = this.controller?.signal;
    this.addEventListener(
      'click',
      (event) => {
        const remove = event.target instanceof Element ? event.target.closest('.rm[data-handle]') : null;
        if (!(remove instanceof HTMLElement)) return;
        removeFromWishlist(remove.dataset.handle || '');
      },
      { signal }
    );
    document.addEventListener('bl:wish', () => this.hasAttribute('open') && this.render(), { signal });
  }

  onOpen() {
    this.render();
  }

  render() {
    const list = this.querySelector('[data-bl-saved-list]');
    const empty = this.querySelector('[data-bl-saved-empty]');
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-bl-saved-row]'));
    if (!list || !template) return;
    const items = readWishlist();
    list.replaceChildren();
    for (const item of items) {
      const row = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const links = row.querySelectorAll('a');
      for (const link of links) link.setAttribute('href', item.u || '#');
      const image = row.querySelector('img');
      if (image) {
        if (item.i) image.src = item.i;
        else image.remove();
      }
      const vendor = row.querySelector('.v');
      if (vendor) vendor.textContent = item.v || '';
      const title = row.querySelector('.t');
      if (title) title.textContent = item.t || item.h;
      const remove = row.querySelector('.rm');
      if (remove instanceof HTMLElement) {
        remove.dataset.handle = item.h;
        remove.setAttribute('aria-label', `${remove.textContent?.trim() || 'Remove'} ${item.t || item.h}`);
      }
      list.appendChild(row);
    }
    if (empty instanceof HTMLElement) empty.hidden = items.length > 0;
  }
}

/* ---------- the drawer's department lists, loaded on the first press of the burger ----------
   snippets/header-drawer.liquid renders the departments as plain links in a <bl-drawer-menu>; the full lists (every
   department's submenu with its groups and links, ~1,400 elements and ~340 KB of every page's HTML before R2) come from
   sections/bl-drawer-menu.liquid through the Section Rendering API, on the current page's URL so the active department
   is marked. The fetch starts when the burger is pressed, hovered or focused, so it is usually back before the drawer
   has slid in; the swap keeps each row's entrance where it is and keyboard focus on its row. A tap on a light row while
   the lists load waits for them (up to DRAWER_ROW_WAIT_MS) and opens that department; a refused or failed request
   (offline, the store's 429) leaves the light rows as plain links, and the next opening asks again. */
const DRAWER_ROW_WAIT_MS = 2500;

/**
 * The CSS Shopify sends with a Section Rendering response (style[data-section-stylesheet]): the {% stylesheet %} CSS
 * of the snippets the lists render (accordion-custom-component), left out of the page's own stylesheet subset when
 * nothing else on the page renders them. Inserted once, right after that subset, so the cascade is the inline lists'.
 * @param {Document} doc
 */
function injectSectionStylesheet(doc) {
  const css = doc.querySelector('style[data-section-stylesheet]');
  if (!css?.textContent || document.querySelector('style[data-bl-drawer-css]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-bl-drawer-css', '');
  style.textContent = css.textContent;
  const subset = document.querySelector('link[rel="stylesheet"][href*="compiled_assets/styles.css"]');
  if (subset) subset.after(style);
  else document.head.prepend(style);
}

class BlDrawerMenu extends HTMLElement {
  /** @type {Promise<boolean> | null} */
  #loading = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get loaded() {
    return this.hasAttribute('data-bl-loaded');
  }

  /**
   * Fetch the full lists once (concurrent calls share the request).
   * @returns {Promise<boolean>} whether the full lists are in
   */
  load() {
    if (this.loaded) return Promise.resolve(true);
    if (this.#loading) return this.#loading;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.set('section_id', this.dataset.section || 'bl-drawer-menu');
    this.#loading = fetch(url.toString())
      .then((response) => {
        if (!response.ok) throw new Error(`drawer lists: ${response.status}`);
        return response.text();
      })
      .then((html) => this.#swap(html))
      .catch(() => {
        this.#loading = null;
        return false;
      });
    return this.#loading;
  }

  /**
   * @param {string} html
   * @returns {boolean}
   */
  #swap(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('ul[data-bl-drawer-lists]');
    const current = this.querySelector(':scope > ul');
    if (!fresh || !current) throw new Error('drawer lists: no list in the response');

    injectSectionStylesheet(doc);

    // the drawer may be open already: each row's entrance (running: its time; over: finished) carries over
    const details = this.closest('details');
    const open = details instanceof HTMLDetailsElement && details.open;
    /** @param {Element} list */
    const rows = (list) => [...list.children].map((li) => li.querySelector('.menu-drawer__animated-element'));
    const entrance = open ? rows(current).map((row) => row?.getAnimations?.()[0]?.currentTime ?? null) : [];
    const focusRow = [...current.children].findIndex((li) => li.contains(document.activeElement));

    current.replaceWith(fresh);
    this.setAttribute('data-bl-loaded', '');

    if (open) {
      rows(fresh).forEach((row, index) => {
        const animation = row?.getAnimations?.()[0];
        if (!animation) return;
        const time = entrance[index];
        if (typeof time === 'number') animation.currentTime = time;
        else animation.finish();
      });
    }
    for (const row of fresh.querySelectorAll('.menu-drawer__animated-element')) {
      row.addEventListener('animationend', () => row instanceof HTMLElement && row.style.setProperty('will-change', 'unset'), { once: true });
    }
    if (focusRow >= 0) {
      const target = fresh.children[focusRow]?.querySelector('summary, a');
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
    return true;
  }

  /** A light row pressed before the lists are in: wait for them and open its department (else follow its link). */
  #onClick = (/** @type {MouseEvent} */ event) => {
    if (this.loaded || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const row = event.target instanceof Element ? event.target.closest('a[data-bl-drawer-row]') : null;
    if (!(row instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    row.setAttribute('aria-busy', 'true');
    const index = Number(row.dataset.blDrawerRow) || 0;
    const timeout = new Promise((resolve) => setTimeout(() => resolve(false), DRAWER_ROW_WAIT_MS));
    Promise.race([this.load(), timeout]).then((ok) => {
      row.removeAttribute('aria-busy');
      const summary = ok ? this.querySelector(`:scope > ul > li:nth-child(${index}) summary`) : null;
      if (summary instanceof HTMLElement) summary.click();
      else window.location.assign(row.href);
    });
  };
}

if (!customElements.get('bl-explore')) customElements.define('bl-explore', BlExplore);
if (!customElements.get('bl-store')) customElements.define('bl-store', BlStore);
if (!customElements.get('bl-store-list')) customElements.define('bl-store-list', BlStoreList);
if (!customElements.get('bl-saved')) customElements.define('bl-saved', BlSaved);
if (!customElements.get('bl-drawer-menu')) customElements.define('bl-drawer-menu', BlDrawerMenu);

/* ---------- page-level wiring ---------- */
function init() {
  syncWishCounts();
  paintStore();

  document.addEventListener('bl:wish', syncWishCounts);
  window.addEventListener('storage', (event) => {
    if (event.key === WISH_KEY) syncWishCounts();
    if (event.key === STORE_KEY) {
      paintStore();
      const current = storeChoice.get();
      if (current) document.dispatchEvent(new CustomEvent('bl:store', { detail: { ...current, source: 'other-tab' } }));
    }
  });
  window.addEventListener('pageshow', syncWishCounts);
  // the drawer's markup is re-rendered by the header's hydration; refresh its counts whenever it opens, and mark the
  // body while the menu drawer is open (Horizon removes [open] once the drawer has slid out)
  const syncDrawerOpen = () => {
    const drawer = document.getElementById('Details-menu-drawer-container');
    document.body.classList.toggle('bl-drawer-open', drawer instanceof HTMLDetailsElement && drawer.open);
  };
  // the drawer's full lists: asked for as soon as the burger is pressed, hovered or focused, and when it opens
  const loadDrawerLists = () => {
    const lists = document.querySelector('bl-drawer-menu');
    if (lists instanceof BlDrawerMenu) lists.load();
  };
  const onBurger = (/** @type {Event} */ event) => {
    if (event.target instanceof Element && event.target.closest('#Details-menu-drawer-container > summary')) loadDrawerLists();
  };
  document.addEventListener('pointerdown', onBurger, { capture: true, passive: true });
  document.addEventListener('pointerover', onBurger, { capture: true, passive: true });
  document.addEventListener('focusin', onBurger, true);
  document.addEventListener(
    'toggle',
    (event) => {
      if (!(event.target instanceof HTMLDetailsElement) || !event.target.closest('header-component')) return;
      if (event.target.id === 'Details-menu-drawer-container' && event.target.open) loadDrawerLists();
      syncWishCounts();
      syncDrawerOpen();
    },
    true
  );
  syncDrawerOpen();

  // the footer's Shop / Help columns fold on phones (sections/bl-footer.liquid); without this script they stay open
  for (const column of document.querySelectorAll('[data-bl-fold]')) column.setAttribute('data-bl-fold-ready', '');
  document.addEventListener('click', (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-bl-fold] .ftr__tg') : null;
    if (!toggle) return;
    const column = toggle.closest('[data-bl-fold]');
    const open = !column?.classList.contains('is-open');
    column?.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  });

  // Horizon measures --top-row-height once; the home header changes height (big logo / row), so keep it current
  const header = document.querySelector('#header-component');
  const topRow = header?.querySelector('.header__row--top');
  if (header instanceof HTMLElement && topRow && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      header.style.setProperty('--top-row-height', `${topRow.getBoundingClientRect().height}px`);
    }).observe(topRow);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
